import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Plus, Sparkles, Wand2, RefreshCw, Pencil, Trash2, Smartphone } from 'lucide-react';
import { Button, ConfirmDialog, ThemedText } from '../../components';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../../components/ModalStyles';
import { FormGroup, FormLabel as Label, StyledSelect as Select } from '../../components/FormStyles';
import { knowledgeBaseService, KB_TOPIC_ICONS } from '../../api';
import { renderFormIcon } from '../../utils/iconUtils';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TopicTreeWrap = styled.div`
  background: ${(p) => p.theme.colors.backgroundSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  padding: ${(p) => p.theme.spacing.md};
`;

const TopicNode = styled.div`
  padding: 8px 10px;
  margin-bottom: 4px;
  border-radius: ${(p) => p.theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border};
`;

const TopicMeta = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.colors.muted};
  margin-top: 2px;
`;

const TopicChildren = styled.div`
  margin-left: 24px;
  margin-top: 4px;
  border-left: 2px solid ${(p) => p.theme.colors.border};
  padding-left: 12px;
`;

const AiBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  background: #e0f2fe;
  color: #075985;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
  font-weight: 600;
`;

const MobileBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  background: #f0fdf4;
  color: #166534;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  padding: 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  max-height: 160px;
  overflow-y: auto;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px solid ${(p) => (p.$selected ? p.theme.colors.tint : 'transparent')};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => (p.$selected ? p.theme.colors.backgroundSecondary : 'transparent')};
  color: ${(p) => p.theme.colors.text};
  cursor: pointer;
  &:hover { background: ${(p) => p.theme.colors.backgroundSecondary}; }
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${(p) => p.theme.colors.text};
  cursor: pointer;
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  margin-bottom: 6px;
  background: ${(p) => p.theme.colors.background};
`;

const InlineIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  cursor: pointer;
  color: ${(p) => p.theme.colors.text};
`;

function flattenForParentSelect(topics, depth = 0, list = []) {
  topics.forEach((t) => {
    list.push({ id: t.id, name: `${'— '.repeat(depth)}${t.name}` });
    if (t.children?.length) flattenForParentSelect(t.children, depth + 1, list);
  });
  return list;
}

function IconPicker({ value, onChange, theme }) {
  return (
    <IconGrid theme={theme}>
      {KB_TOPIC_ICONS.map((name) => (
        <IconButton
          key={name}
          type="button"
          $selected={value === name}
          theme={theme}
          onClick={() => onChange(name)}
          title={name}
        >
          {renderFormIcon(name, 16)}
        </IconButton>
      ))}
    </IconGrid>
  );
}

function TopicNodeRow({ topic, canManage, onEdit, onDelete, theme }) {
  return (
    <>
      <TopicNode theme={theme}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: 13 }}>
            {renderFormIcon(topic.icon || 'file-text', 14, { color: theme.colors.muted })}
            <span>{topic.name}</span>
            {topic.isAiGenerated && <AiBadge>AI</AiBadge>}
            {topic.isVisibleInMobile && (
              <MobileBadge title="Visible in mobile app">
                <Smartphone size={10} /> mobile
              </MobileBadge>
            )}
          </div>
          {(topic.description || topic.documentCount > 0) && (
            <TopicMeta theme={theme}>
              {topic.description}
              {topic.description && topic.documentCount > 0 ? ' · ' : ''}
              {topic.documentCount > 0 ? `${topic.documentCount} document${topic.documentCount === 1 ? '' : 's'}` : ''}
            </TopicMeta>
          )}
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button variant="secondary" size="sm" onClick={() => onEdit(topic)} title="Edit">
              <Pencil size={12} />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onDelete(topic)} title="Delete">
              <Trash2 size={12} />
            </Button>
          </div>
        )}
      </TopicNode>
      {topic.children?.length > 0 && (
        <TopicChildren theme={theme}>
          {topic.children.map((child) => (
            <TopicNodeRow
              key={child.id}
              topic={child}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
              theme={theme}
            />
          ))}
        </TopicChildren>
      )}
    </>
  );
}

const EMPTY_FORM = { name: '', description: '', icon: 'file-text', isVisibleInMobile: false, parentTopicId: '', sortOrder: 0 };

export default function KnowledgeTopicsView({ organizationId, canManage, theme }) {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // AI generation modal state
  const [genOpen, setGenOpen] = useState(false);
  const [genStage, setGenStage] = useState('input'); // 'input' | 'review'
  const [genPrompt, setGenPrompt] = useState('');
  const [genCount, setGenCount] = useState(10);
  const [genBusy, setGenBusy] = useState(false);
  const [genSuggestions, setGenSuggestions] = useState([]);
  const [genIconEditingIdx, setGenIconEditingIdx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await knowledgeBaseService.getTopics(organizationId);
      setTopics(data || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (topic) => {
    setEditing(topic);
    setForm({
      name: topic.name,
      description: topic.description || '',
      icon: topic.icon || 'file-text',
      isVisibleInMobile: !!topic.isVisibleInMobile,
      parentTopicId: topic.parentTopicId ? String(topic.parentTopicId) : '',
      sortOrder: topic.sortOrder || 0,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        icon: form.icon || null,
        isVisibleInMobile: !!form.isVisibleInMobile,
        parentTopicId: form.parentTopicId ? Number(form.parentTopicId) : null,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (editing) {
        await knowledgeBaseService.updateTopic(organizationId, editing.id, payload);
        toast.success('Topic updated');
      } else {
        await knowledgeBaseService.createTopic(organizationId, payload);
        toast.success('Topic created');
      }
      setEditorOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await knowledgeBaseService.deleteTopic(organizationId, deleteTarget.id);
      toast.success('Topic deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Delete failed');
    }
  };

  const openGenerate = () => {
    setGenPrompt('');
    setGenCount(10);
    setGenSuggestions([]);
    setGenStage('input');
    setGenIconEditingIdx(null);
    setGenOpen(true);
  };

  const handleGenerate = async () => {
    setGenBusy(true);
    try {
      const result = await knowledgeBaseService.generateTopicSuggestions(organizationId, {
        prompt: genPrompt.trim() || null,
        count: Number(genCount) || 10,
      });
      const suggestions = (result.suggestions || []).map((s, i) => ({
        ...s,
        selected: true,
        sortOrder: typeof s.sortOrder === 'number' ? s.sortOrder : i,
        icon: s.icon || 'file-text',
      }));
      if (suggestions.length === 0) {
        toast.error('AI returned no suggestions — try a more specific prompt.');
        return;
      }
      setGenSuggestions(suggestions);
      setGenStage('review');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Generation failed');
    } finally {
      setGenBusy(false);
    }
  };

  const handleCommitSelected = async () => {
    const picked = genSuggestions.filter((s) => s.selected);
    if (picked.length === 0) {
      toast.error('Select at least one topic to create.');
      return;
    }
    setGenBusy(true);
    try {
      await knowledgeBaseService.createTopicsBatch(organizationId, {
        markAiGenerated: true,
        topics: picked.map((s, i) => ({
          name: s.name,
          description: s.description || null,
          icon: s.icon || null,
          isVisibleInMobile: !!s.isVisibleInMobile,
          parentTopicId: null,
          sortOrder: typeof s.sortOrder === 'number' ? s.sortOrder : i,
        })),
      });
      toast.success(`Created ${picked.length} topic${picked.length === 1 ? '' : 's'}`);
      setGenOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Create failed');
    } finally {
      setGenBusy(false);
    }
  };

  const updateSuggestion = (idx, patch) => {
    setGenSuggestions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const handleAutoAssign = async () => {
    setBusy(true);
    try {
      const result = await knowledgeBaseService.autoAssignDocuments(organizationId);
      toast.success(`Auto-assigned ${result.assignmentsCreated || 0} document(s) to topics`);
    } catch (err) {
      toast.error(err?.message || 'Auto-assign failed');
    } finally {
      setBusy(false);
    }
  };

  const parentOptions = useMemo(() => flattenForParentSelect(topics), [topics]);

  return (
    <>
      <Toolbar theme={theme}>
        <ThemedText type="default" style={{ color: theme.colors.muted, fontSize: 13 }}>
          {loading ? 'Loading…' : `${parentOptions.length} topic${parentOptions.length === 1 ? '' : 's'}`}
        </ThemedText>
        <ToolbarRight>
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw size={14} /> Refresh
          </Button>
          {canManage && (
            <>
              <Button variant="secondary" size="sm" onClick={openGenerate} disabled={busy}>
                <Sparkles size={14} /> Generate with AI
              </Button>
              <Button variant="secondary" size="sm" onClick={handleAutoAssign} disabled={busy}>
                <Wand2 size={14} /> Auto-assign documents
              </Button>
              <Button variant="primary" size="sm" onClick={openCreate}>
                <Plus size={14} /> New topic
              </Button>
            </>
          )}
        </ToolbarRight>
      </Toolbar>

      <TopicTreeWrap theme={theme}>
        {!loading && topics.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: theme.colors.muted, fontSize: 13 }}>
            No topics yet. {canManage ? 'Click "Generate with AI" or "New topic" to start organizing.' : ''}
          </div>
        )}
        {topics.map((t) => (
          <TopicNodeRow
            key={t.id}
            topic={t}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            theme={theme}
          />
        ))}
      </TopicTreeWrap>

      {/* Create / edit topic modal */}
      <Modal theme={theme} isOpen={editorOpen}>
        <ModalContent theme={theme}>
          <ModalHeader theme={theme}>
            <ThemedText type="subtitle">{editing ? 'Edit topic' : 'New topic'}</ThemedText>
          </ModalHeader>
          <ModalBody theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>Name</Label>
              <Input theme={theme} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <Input
                theme={theme}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Icon</Label>
              <IconPicker value={form.icon} onChange={(icon) => setForm({ ...form, icon })} theme={theme} />
            </FormGroup>
            <FormGroup theme={theme}>
              <ToggleRow theme={theme}>
                <input
                  type="checkbox"
                  checked={form.isVisibleInMobile}
                  onChange={(e) => setForm({ ...form, isVisibleInMobile: e.target.checked })}
                />
                <Smartphone size={14} />
                Visible in mobile app
              </ToggleRow>
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Parent topic</Label>
              <Select
                theme={theme}
                value={form.parentTopicId}
                onChange={(e) => setForm({ ...form, parentTopicId: e.target.value })}
              >
                <option value="">— None (top level) —</option>
                {parentOptions
                  .filter((p) => !editing || p.id !== editing.id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </Select>
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Sort order</Label>
              <Input
                theme={theme}
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter theme={theme}>
            <Button variant="secondary" size="sm" onClick={() => setEditorOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* AI Generate modal — two stages */}
      <Modal theme={theme} isOpen={genOpen}>
        <ModalContent theme={theme} style={{ maxWidth: 640 }}>
          <ModalHeader theme={theme}>
            <ThemedText type="subtitle">
              {genStage === 'input' ? 'Generate topics with AI' : 'Review AI suggestions'}
            </ThemedText>
          </ModalHeader>
          <ModalBody theme={theme}>
            {genStage === 'input' && (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Describe your company / what these documents cover</Label>
                  <TextArea
                    theme={theme}
                    placeholder="e.g. We are a regional water utility managing 30 pumping stations. Our docs cover safety procedures, regulatory compliance, equipment manuals, and quarterly operations reports."
                    value={genPrompt}
                    onChange={(e) => setGenPrompt(e.target.value)}
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>How many topics? (5–20)</Label>
                  <Input
                    theme={theme}
                    type="number"
                    min={5}
                    max={20}
                    value={genCount}
                    onChange={(e) => setGenCount(e.target.value)}
                  />
                </FormGroup>
                <div style={{ fontSize: 12, color: theme.colors.muted }}>
                  AI will use the prompt and your existing documents (if any) to suggest topics with icons. You'll review and pick which ones to create.
                </div>
              </>
            )}
            {genStage === 'review' && (
              <>
                <div style={{ fontSize: 12, color: theme.colors.muted, marginBottom: 8 }}>
                  Edit names, swap icons, and toggle mobile visibility. Untick anything you don't want.
                </div>
                {genSuggestions.map((s, idx) => (
                  <SuggestionRow key={idx} theme={theme}>
                    <input
                      type="checkbox"
                      checked={s.selected}
                      onChange={(e) => updateSuggestion(idx, { selected: e.target.checked })}
                      style={{ marginTop: 6 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <InlineIconButton
                          theme={theme}
                          type="button"
                          onClick={() => setGenIconEditingIdx(genIconEditingIdx === idx ? null : idx)}
                          title="Change icon"
                        >
                          {renderFormIcon(s.icon || 'file-text', 14)}
                        </InlineIconButton>
                        <Input
                          theme={theme}
                          value={s.name}
                          onChange={(e) => updateSuggestion(idx, { name: e.target.value })}
                          style={{ flex: 1 }}
                        />
                        <ToggleRow theme={theme} title="Visible in mobile app">
                          <input
                            type="checkbox"
                            checked={!!s.isVisibleInMobile}
                            onChange={(e) => updateSuggestion(idx, { isVisibleInMobile: e.target.checked })}
                          />
                          <Smartphone size={14} />
                        </ToggleRow>
                      </div>
                      {s.description && (
                        <div style={{ fontSize: 11, color: theme.colors.muted, marginTop: 4 }}>{s.description}</div>
                      )}
                      {genIconEditingIdx === idx && (
                        <div style={{ marginTop: 8 }}>
                          <IconPicker
                            value={s.icon}
                            onChange={(icon) => {
                              updateSuggestion(idx, { icon });
                              setGenIconEditingIdx(null);
                            }}
                            theme={theme}
                          />
                        </div>
                      )}
                    </div>
                  </SuggestionRow>
                ))}
              </>
            )}
          </ModalBody>
          <ModalFooter theme={theme}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (genStage === 'review') {
                  setGenStage('input');
                } else {
                  setGenOpen(false);
                }
              }}
              disabled={genBusy}
            >
              {genStage === 'review' ? 'Back' : 'Cancel'}
            </Button>
            {genStage === 'input' ? (
              <Button variant="primary" size="sm" onClick={handleGenerate} disabled={genBusy}>
                {genBusy ? 'Generating…' : 'Generate'}
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={handleCommitSelected} disabled={genBusy}>
                {genBusy
                  ? 'Creating…'
                  : `Create ${genSuggestions.filter((s) => s.selected).length} selected`}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete topic?"
        message={`Delete "${deleteTarget?.name}"? Topics with sub-topics cannot be deleted.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
